import jwtDecode from 'jwt-decode';
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
  useMemo,
} from 'react';

import { getUser } from '@/core/services/api/userService';

import { UserViewModel } from '../models/user';

import { useLocalStorage } from './useLocalStorage';

interface State {
  user?: UserViewModel | null;
}

const state: State = {};

export const UserContext = createContext<State | null>(null);

export function useUser() {
  const context = useContext(UserContext);

  if (context === null) {
    throw new Error('should be nested under UserProvider');
  }

  return context;
}

/**
 * Проверка прав на действие в интерфейсе.
 *
 * Права на уровне окружения выдаёт backend: он отдаёт пользователю только
 * доступные объекты и отклоняет неразрешённые запросы. Интерфейс дополнительно
 * скрывает действия, доступные лишь администратору.
 *
 * Параметр `authorizations` сохранён в сигнатуре: он описывает проверяемое
 * действие, используется вызывающим кодом и разметкой и остаётся точкой
 * расширения при появлении разграничения прав на стороне интерфейса.
 */
export function useAuthorizations(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  authorizations: string | string[],
  adminOnly = false
) {
  const { user } = useUser();

  return !adminOnly || isAdmin(user);
}

interface AuthorizedProps {
  authorizations: string | string[];
  children: ReactNode;
}

export function Authorized({ authorizations, children }: AuthorizedProps) {
  const isAllowed = useAuthorizations(authorizations);

  return isAllowed ? <>{children}</> : null;
}

interface UserProviderProps {
  children: ReactNode;
}

export function UserProvider({ children }: UserProviderProps) {
  const [jwt] = useLocalStorage('JWT', '');
  const [user, setUser] = useState<UserViewModel | null>(null);

  useEffect(() => {
    if (state.user) {
      setUser(state.user);
    } else if (jwt !== '') {
      const tokenPayload = jwtDecode(jwt) as { id: number };

      loadUser(tokenPayload.id);
    }
  }, [jwt]);

  const providerState = useMemo(() => ({ user }), [user]);

  if (jwt === '') {
    return null;
  }

  if (providerState.user === null) {
    return null;
  }

  return (
    <UserContext.Provider value={providerState}>
      {children}
    </UserContext.Provider>
  );

  async function loadUser(id: number) {
    const user = await getUser(id);
    state.user = user;
    setUser(user);
  }
}

function isAdmin(user?: UserViewModel | null) {
  return !!user && user.Role === 1;
}
